import React, { useState } from "react";
import "./FundraiserInfoForm.css";
import puzzled_bear from "../../../../images/Starting-Your-Fundraiser/puzzled-bear.png";
import bear_pointing from "../../../../images/Starting-Your-Fundraiser/bear-pointing.png";
import MultiCheckbox, { Checkbox } from "./Checkbox";
import {
  sanitizeEmailInput,
  sanitizeTextInput,
} from "../../../../CommonFunctions";
import {
  Fundraiser,
  FundraiserResponse,
  FundraiserSubsequentInfo,
} from "../../../../Interfaces/Fundraiser";
import { useNavigate } from "react-router-dom";
import config from "../../../../config";
import { productCategories } from "../../../../CommonFunctions";
import ShippingForm from "../../../../components/Checkout/CheckoutPageComponents";
import { ShippingInfo } from "../../../../Interfaces/ShippingInfo";
import { getCurrentlyLoggedInUser } from "../../../../CommonFunctions";
import apiClient from "../../../../services/apiClient";
const BASE_API = config.API_BASE_URL;

export interface FundraiserInfoProps {
  organization_id: number;
  coordinator_name: string;
  coordinator_email: string;
  file_location_ref: string | null;
  primary_color: string;
  secondary_color: string;
  payment_method_id: number;
  teamAdminName: string;
  primaryDivisionName: string;
  secondaryDivisionName: string;
}

interface ShippingFormProps {
  setIsCheckoutEnabled: (value: boolean) => void;
  setShippingInfo: React.Dispatch<React.SetStateAction<ShippingInfo>>;
}

const FundraiserInfoForm: React.FC<FundraiserInfoProps> = (props) => {
  const [isCheckoutEnabled, setIsCheckoutEnabled] = useState(false);
  const [shippingInfo, setShippingInfo] = useState<ShippingInfo>({
    ship_to_name: "",
    street_address: "",
    apt_suite: "",
    city: "",
    state: "",
    zip_code: "",
  });
  const navigate = useNavigate();

  const fetchLoggedInAccount = async () => {
    try {
      const user = await getCurrentlyLoggedInUser();
      return user;
    } catch (error) {
      navigate("/login");
    }
  };
  const user = fetchLoggedInAccount();

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const organization_id = props.organization_id;
    const coordinator_name = props.coordinator_name;
    const coordinator_email = props.coordinator_email;
    const file_location_ref = props.file_location_ref;
    const primary_color = props.primary_color;
    const secondary_color = props.secondary_color;
    const payment_method_id = props.payment_method_id;
    const teamAdminName = props.teamAdminName;
    const primaryDivisionName = props.primaryDivisionName;
    const secondaryDivisionName = props.secondaryDivisionName;
    let shippingID: number | null = null;

    if (formData.allow_ship_to_org && shippingInfo.state) {
      try {
        const shippingResponse = await apiClient.post<ShippingInfo>(
          BASE_API + "shipping-address/",
          shippingInfo,
          {
            headers: {
              "Content-Type": "application/json",
            },
          }
        );
        shippingID = (await shippingResponse.data.id) || null;
      } catch {
        console.log("Shipping Record Creation Failed");
        return;
      }
    }

    //Create the Fundraiser
    const fundraiser_to_create: Fundraiser = {
      org: organization_id,
      fundraiser_name: sanitizeTextInput(formData.fundraiserName),
      rep: (await user)?.id || null,
      coordinator: null,
      start_date: formData.startDate,
      end_date: formData.endDate,
      goal:
        parseInt(formData.participantGoal, 10) *
        parseInt(formData.participantCount, 10),
      participant_buyout: formData.buyout ? parseInt(formData.buyoutAmt) : 0,
      auto_extend_goal: formData.autoExtend,
      participant_est_cnt: parseInt(formData.participantCount, 10),
      primary_color: primary_color,
      secondary_color: secondary_color,
      coord_email: sanitizeEmailInput(coordinator_email),
      payment_method: payment_method_id,
      fundraiser_purpose_message: sanitizeTextInput(formData.fundraiserPurpose),
      allow_brochure: formData.allow_brochure,
      allow_ship_to_org: formData.allow_ship_to_org,
      allow_ship_to_participant: formData.allow_ship_to_participant,
      org_shipping_info: shippingID,
    };
    const response = await apiClient.post<FundraiserResponse>(
      BASE_API + "fundraisers",
      fundraiser_to_create,
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
    const fundraiserID = response.data.id;

    const blackListedCategories = checkboxes
      .filter((checkbox) => checkbox.isChecked)
      .map((checkbox) => checkbox.value);

    const categoryData = {
      category_names: blackListedCategories,
    };

    if (blackListedCategories.length > 0) {
      try {
        const categoryResponse = await apiClient.post(
          `${BASE_API}fundraiser/${fundraiserID}/categories/blacklist`,
          categoryData,
          {
            headers: {
              "Content-Type": "application/json",
            },
          }
        );
      } catch {
        console.log("An error occured with category blacklist");
      }
    }

    const fundraiserInfo: FundraiserSubsequentInfo = {
      fundraiserID: fundraiserID,
      teamAdminName: teamAdminName,
      primaryDivisionName: primaryDivisionName,
      secondaryDivisionName: secondaryDivisionName,
    };

    navigate("/create-fundraiser/division-info", { state: fundraiserInfo });
  };

  const [formData, setFormData] = useState({
    fundraiserName: "",
    startDate: "",
    endDate: "",
    participantCount: "0",
    participantGoal: "0",
    buyoutAmt: "0",
    autoExtend: true,
    fundraiserPurpose: "",
    allow_brochure: false,
    allow_ship_to_org: false,
    allow_ship_to_participant: false,
    buyout: false,
  });

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = e.target;
    setFormData({
      ...formData,
      [name]: type === "radio" ? value === "true" : value,
    });
    if (name === "allow_ship_to_org" && value === "true") {
      setIsCheckoutEnabled(false);
    }
  };

  const handleCheckboxToggle = (id: number) => {
    const updatedCheckboxes = checkboxes.map((checkbox) =>
      checkbox.id === id
        ? { ...checkbox, isChecked: !checkbox.isChecked }
        : checkbox
    );
    setCheckboxes(updatedCheckboxes);
  };

  const getCheckBoxChoices = (): Checkbox[] => {
    return productCategories.map((category, index) => ({
      id: index + 1,
      value: category[0],
      label: category[1],
      isChecked: false,
    }));
  };

  const [checkboxes, setCheckboxes] = useState<Checkbox[]>(
    getCheckBoxChoices()
  );

  return (
    <div className="form-wrapper">
      <div className="form-container">
        <form onSubmit={handleSubmit}>
          <div className="input-group">
            <label className="input-label" htmlFor="fundraiserName">
              Fundraiser Name*
            </label>
            <input
              type="text"
              id="fundraiserName"
              name="fundraiserName"
              value={formData.fundraiserName}
              onChange={handleChange}
              placeholder="Fundraiser Name"
              required
            />
          </div>
          <div className="multi-select-group">
            <label className="multi-select-label" htmlFor="multiSelect">
              Which collections would you like to opt out:
            </label>
            <div className="select-and-image">
              <MultiCheckbox
                checkboxes={checkboxes}
                onToggle={handleCheckboxToggle}
              />
              <div className="image-container">
                <img src={puzzled_bear} alt="Puzzled Bear" />
              </div>
            </div>
          </div>
          <div className="date-select-group">
            <div className="date-input-group">
              <label className="date-input-label" htmlFor="startDate">
                Start Date*
              </label>
              <input
                type="date"
                id="startDate"
                name="startDate"
                value={formData.startDate}
                onChange={handleChange}
                className="date-input"
                required
              />
            </div>
            <div className="date-input-group">
              <label className="date-input-label" htmlFor="endDate">
                End Date*
              </label>
              <input
                type="date"
                id="endDate"
                name="endDate"
                value={formData.endDate}
                onChange={handleChange}
                className="date-input"
                required
              />
            </div>
          </div>
          <div className="estimation-group">
            <label className="estimation-label" htmlFor="participantCount">
              How many participants are there in your organization? (Estimation
              is okay)
            </label>
            <input
              type="number"
              id="participantCount"
              name="participantCount"
              value={formData.participantCount}
              onChange={handleChange}
              className="estimation-input"
            />
          </div>
          <div className="goal-group">
            <label className="goal-label" htmlFor="participantGoal">
              What is each participant's profit goal?
            </label>
            <input
              type="number"
              id="participantGoal"
              name="participantGoal"
              value={formData.participantGoal}
              onChange={handleChange}
              className="goal-input"
            />
          </div>
          <div className="yesno-group">
            <label className="yesno-label" htmlFor="autoExtend">
              If a seller reaches their goal before the end of their fundraiser,
              should their goal autoextend to display a higher amount?
            </label>
            <div className="yesno-inputs">
              <label className="yesno-input">
                <input
                  type="radio"
                  id="autoExtendYes"
                  name="autoExtend"
                  value="true"
                  checked={formData.autoExtend === true}
                  onChange={handleChange}
                />
                Yes
              </label>
              <label className="yesno-input">
                <input
                  type="radio"
                  id="autoExtendNo"
                  name="autoExtend"
                  value="false"
                  checked={formData.autoExtend === false}
                  onChange={handleChange}
                />
                No
              </label>
            </div>
          </div>
          <div className="yesno-group">
            <label className="yesno-label" htmlFor="allow_brochure">
              Do you need brochures?
            </label>
            <div className="yesno-inputs">
              <label className="yesno-input">
                <input
                  type="radio"
                  id="allow_brochureYes"
                  name="allow_brochure"
                  value="true"
                  checked={formData.allow_brochure === true}
                  onChange={handleChange}
                />
                Yes
              </label>
              <label className="yesno-input">
                <input
                  type="radio"
                  id="allow_brochureNo"
                  name="allow_brochure"
                  value="false"
                  checked={formData.allow_brochure === false}
                  onChange={handleChange}
                />
                No
              </label>
            </div>
          </div>
          <div className="yesno-group">
            <label className="yesno-label" htmlFor="allow_ship_to_org">
              Allow ship to organization?
            </label>
            <div className="yesno-inputs">
              <label className="yesno-input">
                <input
                  type="radio"
                  id="allow_ship_to_orgYes"
                  name="allow_ship_to_org"
                  value="true"
                  checked={formData.allow_ship_to_org === true}
                  onChange={handleChange}
                  disabled={formData.allow_ship_to_participant === true}
                />
                Yes
              </label>
              <label className="yesno-input">
                <input
                  type="radio"
                  id="allow_ship_to_orgNo"
                  name="allow_ship_to_org"
                  value="false"
                  checked={formData.allow_ship_to_org === false}
                  onChange={handleChange}
                />
                No
              </label>
            </div>
          </div>
          {/*
            Ship to participant is not ready so commenting out the choice for now
          <div className="yesno-group">
            <label className="yesno-label" htmlFor="allow_ship_to_participant">
              Allow ship to participant?
            </label>
            <div className="yesno-inputs">
              <label className="yesno-input">
                <input
                  type="radio"
                  id="allow_ship_to_participantYes"
                  name="allow_ship_to_participant"
                  value="true"
                  checked={formData.allow_ship_to_participant === true}
                  onChange={handleChange}
                  disabled={formData.allow_ship_to_org === true}
                />
                Yes
              </label>
              <label className="yesno-input">
                <input
                  type="radio"
                  id="allow_ship_to_participantNo"
                  name="allow_ship_to_participant"
                  value="false"
                  checked={formData.allow_ship_to_participant === false}
                  onChange={handleChange}
                />
                No
              </label>
            </div>
          </div>
          */}
          <div style={{ display: "flex", flexDirection: "column" }}>
            <div className="yesno-group">
              <label className="yesno-label" htmlFor="buyout">
                If the participant doesn't raise their minimum do you want us to
                handle the buyout/donation?
              </label>
              <div className="yesno-inputs">
                <label className="yesno-input">
                  <input
                    type="radio"
                    id="buyoutYes"
                    name="buyout"
                    value="true"
                    checked={formData.buyout === true}
                    onChange={handleChange}
                  />
                  Yes
                </label>
                <label className="yesno-input">
                  <input
                    type="radio"
                    id="buyoutNo"
                    name="buyout"
                    value="false"
                    checked={formData.buyout === false}
                    onChange={handleChange}
                  />
                  No
                </label>
              </div>
            </div>
            {formData.buyout && (
              <div className="buyout-group">
                <label className="buyout-label" htmlFor="participantBuyout">
                  What is each participant's buyout/donation amount?
                </label>
                <input
                  type="number"
                  id="participantBuyout"
                  name="buyoutAmt"
                  value={formData.buyoutAmt}
                  onChange={handleChange}
                  className="buyout-input"
                />
              </div>
            )}
          </div>

          <div className="textarea-group">
            <label className="textarea-label" htmlFor="fundraiserPurpose">
              Please describe what you are raising money for (this content will
              be shown to new parents and participants who join the
              fundraiser):*
            </label>
            <textarea
              id="fundraiserPurpose"
              name="fundraiserPurpose"
              value={formData.fundraiserPurpose}
              onChange={handleChange}
              className="textarea-input"
              placeholder="Message"
              required
            />
          </div>

          <div className="submit-container">
            <img
              src={bear_pointing}
              alt="bear-pointing"
              className="centered-image"
            />
            <input
              type="submit"
              value="Submit"
              className="submit-button"
              disabled={formData.allow_ship_to_org && !isCheckoutEnabled}
            />
          </div>
        </form>
        {formData.allow_ship_to_org && (
          <div>
            <h1>Organization Shipping info</h1>
            <ShippingForm
              setIsCheckoutEnabled={setIsCheckoutEnabled}
              setShippingInfo={setShippingInfo}
              isFromCreateFundraiser={true}
              needShippingInfo={true}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default FundraiserInfoForm;
